/**
 * Site dashboard quick-action command resolution.
 *
 * Keeps the one-tap dashboard commands aligned with the server-provided
 * command catalog. The catalog states which status frame the unit answers
 * with, so that answer is what gets sent; protocol-specific preference
 * (supports_v2_protocol) only settles the cases it doesn't cover.
 *
 * Contains:
 * - QUICK_ACTION_KEY_SETS — candidate command keys per quick action (unordered pool)
 * - STATUS_FRAME_KEYS — the primary status frames, most-capable first
 * - availableStatusFrameKeys — catalog-present status frames, in that order
 * - resolveStatusKey — unit-aware status frame resolution
 * - resolveStatusDispatch — full status dispatch precedence (declared / picker / server-resolved)
 * - resolveLivePowerKey — live-power key resolution, falling back to resolveStatusKey
 * - resolveQuickActionKey — first available command key from a candidate list
 */

export const QUICK_ACTION_KEY_SETS = {
  status: ['STATUS_GET_V2', 'STATUS_GET_ZONEINFO', 'STATUS_GET_SHORT'],
  livePower: ['STATUS_GET_LIVE_POWER', 'STATUS_GET_V2', 'STATUS_GET_ZONEINFO', 'STATUS_GET_SHORT'],
  reboot: ['RESET'],
};

/**
 * The primary targeted status frames, most-capable first. Mirrors the
 * catalog's status section; the catalog exposes more than one of these to
 * staff whenever firmware is unknown or a declared command is absent, which
 * is why resolveStatusDispatch routes that case to a manual picker instead
 * of guessing.
 */
export const STATUS_FRAME_KEYS = ['STATUS_GET_V2', 'STATUS_GET_ZONEINFO', 'STATUS_GET_SHORT', 'STATUS_GET_LONG'];

/**
 * Status-frame keys present in the catalog, in STATUS_FRAME_KEYS order.
 *
 * @param {object} commandsByKey - map of command key → command object
 * @returns {string[]}
 */
export function availableStatusFrameKeys(commandsByKey) {
  return STATUS_FRAME_KEYS.filter(key => commandsByKey?.[key]);
}

/**
 * Resolve the best status-frame key for a unit.
 *
 * A `preferred` key — the frame the backend resolved for this unit, whether
 * from a firmware declaration, the protocol generation, or the customer type
 * — wins outright whenever the catalog actually offers it. The backend has
 * the unit in hand, so the frontend defers rather than re-guessing. Absent a
 * usable preferred key, `supportsV2` is the only capability signal consulted:
 * catalog presence is never treated as a proxy for firmware capability.
 * supportsV2 true prefers STATUS_GET_V2 when present, else falls back to
 * ZONEINFO / SHORT only (never LONG — a V2 unit never falls back to the
 * V1-only diagnostic frame). Anything else (false, null, undefined) never
 * picks V2, falling back among ZONEINFO / SHORT / LONG in that order.
 *
 * Resolves only among keys present in commandsByKey (catalog-available).
 *
 * @param {object} commandsByKey - map of command key → command object
 * @param {boolean|null|undefined} supportsV2 - unit.firmware_version.supports_v2_protocol
 * @param {string|null} [preferred] - the backend's resolved status frame, if any
 * @returns {string|null}
 */
export function resolveStatusKey(commandsByKey, supportsV2, preferred = null) {
  if (preferred && commandsByKey?.[preferred]) {
    return preferred;
  }
  if (supportsV2 === true) {
    if (commandsByKey?.['STATUS_GET_V2']) return 'STATUS_GET_V2';
    return ['STATUS_GET_ZONEINFO', 'STATUS_GET_SHORT'].find(key => commandsByKey?.[key]) ?? null;
  }
  return ['STATUS_GET_ZONEINFO', 'STATUS_GET_SHORT', 'STATUS_GET_LONG'].find(key => commandsByKey?.[key]) ?? null;
}

/**
 * Resolve the status quick-action's full dispatch precedence.
 *
 * `resolved` is the catalog's `default_status` — `{key, source}` — where
 * source is `declared` (a firmware row states the frame), `firmware` (the
 * unit's protocol generation settles it) or `customer_type` (the unit has
 * never reported firmware, so the customer's billboard/commercial split is
 * the best available signal).
 *
 * A `declared` frame sends immediately — for staff and non-staff alike,
 * skipping the manual picker entirely: an explicit declaration is not a
 * guess. Otherwise staff see a picker whenever their catalog (not
 * firmware-filtered) offers more than one primary frame, since no signal
 * separates those frames for them. Everyone else, and staff with at most one
 * frame available, sends the backend's resolved frame. Catalog presence order
 * is never used as a capability guess for the send path — that was the
 * ENG-840 bug, and re-deriving it here would reintroduce it one layer down.
 *
 * @param {object} params
 * @param {{key: string, source: string}|null} [params.resolved] - catalog `default_status`
 * @param {boolean} params.isStaff
 * @param {object} params.commandsByKey - map of command key → command object
 * @param {boolean|null|undefined} params.supportsV2 - unit.firmware_version.supports_v2_protocol
 * @returns {{type: 'send', key: string}|{type: 'choose', frames: string[]}|{type: 'none'}}
 */
export function resolveStatusDispatch({ resolved, isStaff, commandsByKey, supportsV2 }) {
  const resolvedKey = resolved?.key ?? null;
  if (resolved?.source === 'declared' && commandsByKey?.[resolvedKey]) {
    return { type: 'send', key: resolvedKey };
  }
  const frames = availableStatusFrameKeys(commandsByKey);
  if (isStaff && frames.length > 1) {
    return { type: 'choose', frames };
  }
  const key = resolveStatusKey(commandsByKey, supportsV2, resolvedKey);
  return key ? { type: 'send', key } : { type: 'none' };
}

/**
 * Resolve the best live-power key for a unit.
 *
 * Prefers STATUS_GET_LIVE_POWER when available; falls back to the same
 * unit-aware status frame resolveStatusKey would pick (STATUS_GET_V2 on v2
 * units carries the unified frame that includes power data).
 *
 * @param {object} commandsByKey - map of command key → command object
 * @param {boolean|null|undefined} supportsV2 - unit.firmware_version.supports_v2_protocol
 * @param {string|null} [preferred] - the backend's resolved status frame, if any
 * @returns {string|null}
 */
export function resolveLivePowerKey(commandsByKey, supportsV2, preferred = null) {
  if (commandsByKey?.['STATUS_GET_LIVE_POWER']) return 'STATUS_GET_LIVE_POWER';
  return resolveStatusKey(commandsByKey, supportsV2, preferred);
}

/**
 * First available command key from an ordered candidate list.
 * Used for catalog-agnostic specs (e.g. reboot) that don't need v2 awareness.
 *
 * @param {object} commandsByKey - map of command key → command object
 * @param {string[]} keys - ordered candidate keys
 * @returns {string|null}
 */
export function resolveQuickActionKey(commandsByKey, keys) {
  return keys.find(candidate => commandsByKey?.[candidate]) ?? null;
}
